// The Studio's commands for a Saju book's manuscripts (specs/011 §5, P6): list the scopes and the facts to write them
// from, write a scope's text as a draft — an agent's (write_narrative) or a person's in the review tab — and mark it
// reviewed, which only the review tab does: no agent command reviews. Writing is local: the facts come from the Saju
// engine and the citations are checked against the wiki, and both are only on the machine that runs the workshop.
import { agentError, agentObject } from './agent-bridge.mjs';
import { boxText, narrativeFacts } from './component-saju-narrative.mjs';
import { narrativeScopes, scopeStale, writeRefusals, writtenProps } from './narrative-writing.mjs';
import { loadPrintMetrics } from './print-wrap.mjs';
import { sajuBookOutput } from './saju-book-output.mjs';

const WORKSHOP = '/__saju/book';
const FACT_PATHS = '근거 경로는 facts 안의 이름을 점과 [번호]로 잇습니다: daeun.ten_god_stem, natal.pillars[2].stem';

async function local() {
  if (!await sajuBookOutput.workshop.available()) throw agentError('LOCAL_ONLY', '사주 원고는 로컬 제작실에서만 씁니다 — 사실을 계산하는 사주 엔진과 인용을 확인하는 wiki 가 이 PC 에만 있습니다.');
}
async function workshop(path, body) {
  const response = await fetch(`${WORKSHOP}${path}`, body === undefined ? { cache: 'no-store' }
    : { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const value = await response.json().catch(() => null);
  if (!response.ok || !value) throw agentError('WORKSHOP', value?.error ?? `로컬 제작실이 답하지 않았습니다 (${response.status}).`);
  return value;
}

/** The lines a box may take: its height when the book gave one, else as many as the sample printed. */
function lineBudget(metrics, box) {
  if (box.height === null) return box.lines;
  let lines = 1;
  while (lines < 400 && metrics.fits(lines + 1, box.size, box.height)) lines += 1;
  return lines;
}

/**
 * `getContent()` the document; `openPage(section)` makes a page the active one; `upsert(input)` the workspace's
 * upsertComponent; `reviewer()` who reviews (the signed-in account, or 'local'); `asOneChange(action)` makes a change
 * across pages one change.
 */
export function createNarrativeCommands({ getContent, openPage, upsert, getAccessToken, reviewer, asOneChange }) {
  function scopeOf({ scope, componentId } = {}) {
    if (!scope || typeof scope !== 'object') throw agentError('INVALID_INPUT', 'scope 는 list_narratives 가 준 범위입니다.');
    const groups = narrativeScopes(getContent()).filter(group => JSON.stringify(group.scope) === JSON.stringify(scope)
      && (componentId === undefined || group.instances.some(instance => instance.component.id === componentId)));
    if (!groups.length) throw agentError('NOT_FOUND', '그 범위의 원고가 이 문서에 없습니다.');
    if (groups.length > 1) throw agentError('AMBIGUOUS', '그 범위의 원고가 두 사람 것입니다 — componentId 로 하나를 골라 주세요.');
    return groups[0];
  }
  // A scope's instances page by page, as one change: its text differs between pages until the last one is written.
  async function eachInstance(group, propsOf) {
    await asOneChange(async () => {
      for (const section of [...new Set(group.instances.map(instance => instance.section))]) {
        await openPage(section);
        for (const { component } of group.instances.filter(instance => instance.section === section)) {
          await upsert({ id: component.id, type: component.type, props: propsOf(component) });
        }
      }
    });
  }

  /** The scopes to write ('stale'), written and waiting for review ('draft'), or all, with the facts and room for each slot. */
  async function list({ state = 'all' } = {}) {
    if (!['all', 'stale', 'draft', 'reviewed', 'sample'].includes(state)) throw agentError('INVALID_INPUT', 'state 는 all·stale·draft·reviewed·sample 입니다.');
    await local();
    const metrics = await loadPrintMetrics();
    const groups = narrativeScopes(getContent()).filter(group => state === 'all' || (state === 'stale' ? scopeStale(group) : group.review.state === state));
    const narratives = [];
    for (const group of groups) {
      const facts = await narrativeFacts(group.instances[0].component.props, { getAccessToken });
      narratives.push({ scope: group.scope, label: group.label, pages: [...new Set(group.instances.map(instance => instance.page))],
        state: group.review.state, stale: scopeStale(group), factsHash: group.factsHash, facts,
        ...(group.basis ? { basis: group.basis } : {}), ...(group.citations ? { citations: group.citations } : {}), ...(group.generator ? { generator: group.generator } : {}),
        slots: Object.entries(group.slots).map(([address, text]) => ({ address, text, boxes: group.instances.flatMap(({ page, component }) => component.props.boxes
          .filter(box => box.segments.some(segment => segment.text === address))
          .map(box => ({ page, box: box.id, font: box.font, size: box.size, width: box.width, maxLines: lineBudget(metrics, box),
            printedLines: metrics.wrap(boxText(component.props, component.snapshot, box), box.size, box.width, box.font).length }))) })) });
    }
    return { narratives, guides: (await workshop('/wiki/guides')).guides, factPaths: FACT_PATHS };
  }

  /** Writes a scope's text on every page it prints as a draft; `person` for the review tab's edit (no basis, citations or writer asked). */
  async function write(input, { person = false } = {}) {
    agentObject(input);
    await local();
    const group = scopeOf(input);
    await loadPrintMetrics();
    const facts = person ? null : await narrativeFacts(group.instances[0].component.props, { getAccessToken });
    const cited = Array.isArray(input.citations) ? input.citations.map(item => item?.wiki).filter(path => typeof path === 'string') : [];
    const missingWiki = person || !cited.length ? [] : (await workshop('/wiki/check', { paths: cited })).missing;
    const refusals = writeRefusals(group, person ? { ...input, expectedFactsHash: group.factsHash } : input, { facts, missingWiki, person });
    if (refusals.length) throw Object.assign(agentError('NARRATIVE_REFUSED', `원고를 쓰지 않았습니다 — ${refusals.map(refusal => refusal.message).join(' / ')}`), { refusals });
    const record = person ? { basis: group.basis, citations: group.citations, generator: { agent: '사람', guide: '원고 탭' } }
      : { basis: input.basis, citations: input.citations, generator: input.generator };
    await eachInstance(group, component => writtenProps(component, input.text, group.factsHash, record));
    return { scope: group.scope, label: group.label, pages: [...new Set(group.instances.map(instance => instance.page))], instances: group.instances.length, state: 'draft' };
  }

  /** The review tab's 검토 완료: a text written from the facts now becomes reviewed, on every page, by whom and when. */
  async function review(input) {
    const group = scopeOf(input);
    if (scopeStale(group)) throw agentError('STALE', '지금 명식의 사실로 쓴 글이 아니라 검토를 마칠 수 없습니다 — 먼저 다시 써 주세요.');
    const done = { state: 'reviewed', by: await reviewer(), at: new Date().toISOString() };
    await eachInstance(group, component => ({ ...component.props, text: { ...component.props.text, review: done } }));
    return { scope: group.scope, pages: [...new Set(group.instances.map(instance => instance.page))], state: 'reviewed' };
  }

  return { list, write, review, scopeOf };
}
